// THE WALLETS MONEY SCREEN (UI rebuild; the operator's yes in the wallet session, 28 Oct 2026 — "yes send it to the CC"). Custody's landing in the
// rebuilt console, and what /custody/balances answers (every "Open Wallets" button in Telegram lands there). Switched off with custody
// (CUSTODY_IN_CONSOLE): nothing here is reachable until the Worker's switch and the console's go on together.
//
// Four blocks, Astra-light:
//   1  one status line — what the Mac last read on TRON, the fee wallet's TRX, whether sweeps run now
//   2  Needs you, only when something waits — payouts to sign, deposits held, money in Flagged: one line and one button each
//   3  where the money sits — one row per role (Treasury, Hot with its day cap, Merchant Hot, Flagged, Deposit addresses, Fee wallet); a row opens its
//      wallets
//   4  today — the Books' money movements of the Dubai day, the last few, then All moves
// The decisions are components/v2/custody/money.ts (pure, tested). A figure no read gives is said as not known, never made up.
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api, custody, type WalletBalance } from '../../../lib/api';
import { WalletMovesSheet } from '../../../components/v2/custody/WalletMoves';
import { readState } from '../../../lib/read-state';
import { partyName } from '../../../lib/naming';
import { dubaiDate, fmtMoney, fmtTime } from '../../../lib/format';
import { relativeTime, walletName } from '../../../lib/custody-format';
import { needLines, roleRows, statusParts, type Role, type RoleRow } from '../../../components/v2/custody/money';
import { DetailSheet, EmptyState, PageHead, Panel, ReadFailed, SkeletonRows, StateLine, TAP } from '../../../components/v2/kit';

const TODAY_SHOWN = 5;

export function MoneyV2() {
  const [search, setSearch] = useSearchParams();
  const summary = useQuery({ queryKey: ['custody', 'summary'], queryFn: custody.summary, refetchInterval: 60_000 });
  const balances = useQuery({ queryKey: ['custody', 'balances'], queryFn: custody.balances, refetchInterval: 60_000 });
  const approvals = useQuery({ queryKey: ['custody', 'approvals'], queryFn: custody.approvals, refetchInterval: 15_000 });
  const deposits = useQuery({ queryKey: ['custody', 'deposits'], queryFn: () => custody.deposits(), refetchInterval: 60_000 });
  // A 503 limits_unset is "no cap set" — not known, said so on the Hot row; no retry storm over it.
  const limits = useQuery({ queryKey: ['custody', 'limits'], queryFn: custody.limits, retry: false });
  const today = dubaiDate();
  // Whom a deposit address was issued to, by name (never a slug): the same roster read the Parties screen keeps (one cache entry).
  const parties = useQuery({ queryKey: ['parties', 'all', '', false], queryFn: () => api.listParties({ entity: 'dubaiotc', pageSize: 500 }) });
  const nameOf = (slug: string) => partyName({ slug, display_name: parties.data?.parties.find((p) => p.slug === slug)?.display_name ?? null });
  const moves = useQuery({ queryKey: ['movements', 'custody-today', today], queryFn: () => api.movements({ from: today, to: today, limit: TODAY_SHOWN }) });

  const bs = readState(balances);
  const rows = balances.data ? roleRows({ balances: balances.data.wallets, deposits: deposits.data?.deposits ?? null, limits: limits.data ?? null, summary: summary.data ?? null }) : [];
  const needs = needLines({
    approvals: readState(approvals) === 'known' ? approvals.data!.approvals : null,
    deposits: readState(deposits) === 'known' ? deposits.data!.deposits : null,
    balances: bs === 'known' ? balances.data!.wallets : null,
  });
  const parts = statusParts({
    freshness: balances.data?.freshness ?? null, asOf: balances.data?.asOf ?? null, summary: summary.data ?? null,
    now: Date.now(), relative: (iso, now) => relativeTime(iso, now), time: (iso) => fmtTime(iso),
  });
  const openRole = search.get('role') as Role | null;
  const open = openRole ? rows.find((r) => r.role === openRole) ?? null : null;
  const setRole = (r: Role | null) => { const s = new URLSearchParams(search); if (r) s.set('role', r); else s.delete('role'); s.delete('wallet'); setSearch(s); };
  // ONE WALLET'S MOVES (the wallet's P-073, 3 Nov 2026): a role of one wallet opens straight to them; a role of several lists its
  // wallets, and each opens its own. Kept in ?wallet= so Back returns to the list.
  const walletId = search.get('wallet');
  const openWallet = walletId ? (balances.data?.wallets ?? []).find((w) => String(w.id) === walletId) ?? null : null;
  const setWallet = (id: number | null) => { const s = new URLSearchParams(search); if (id === null) s.delete('wallet'); else s.set('wallet', String(id)); setSearch(s); };
  const tapRole = (r: RoleRow) => { const s = new URLSearchParams(search); s.set('role', r.role); if (r.wallets.length === 1) s.set('wallet', String(r.wallets[0]!.id)); else s.delete('wallet'); setSearch(s); };

  return (
    <>
      {/* A stalled TRON head is said in amber (P-072): the reads may be fresh while the chain stands still. */}
      <PageHead title="Wallets" sub={<span>{parts.map((p, n) => <span key={p}>{n > 0 && ' · '}<span className={/^TRON stalled/.test(p) ? 'text-warn' : undefined}>{p}</span></span>)}</span>} />

      {/* 2 · NEEDS YOU — only when something waits; a read that failed adds nothing here (its own line says it failed). */}
      {needs.length > 0 && (
        <Panel title="Needs you" aside={needs.length} className="mb-4 lg:mb-6" flush>
          <ul>
            {needs.map((n) => (
              <li key={n.key} className="flex min-h-14 items-center justify-between gap-3 border-b border-stroke-soft px-4 py-2 last:border-b-0 lg:px-5">
                <span className="min-w-0 text-body">{n.words}</span>
                <Link to={n.to} className="btn-secondary shrink-0 px-3 text-body">{n.action}</Link>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {/* 3 · WHERE THE MONEY SITS — one row per role. */}
      <Panel title="Where the money sits" flush className="mb-4 lg:mb-6">
        {bs === 'waiting' && !balances.data && <SkeletonRows rows={5} what="the wallets" />}
        {bs === 'failed' && <div className="px-4 py-3 lg:px-5"><ReadFailed what="the wallets" error={balances.error} onRetry={() => void balances.refetch()} tone={balances.data ? 'warn' : 'bad'} /></div>}
        {balances.data && rows.length === 0 && <EmptyState>No custody wallets are registered yet.</EmptyState>}
        {rows.length > 0 && (
          <ul>
            {rows.map((r) => (
              <li key={r.role} className="border-b border-stroke-soft last:border-b-0">
                <button type="button" onClick={() => tapRole(r)}
                  className="flex min-h-14 w-full items-center justify-between gap-4 px-4 py-3 text-left hover:bg-panel-2 lg:px-5">
                  <span className="min-w-0">
                    <span className="block text-body font-medium">{r.word}</span>
                    {r.note && <span className="mt-0.5 block text-meta text-tg-hint">{r.note}</span>}
                  </span>
                  <span className="shrink-0 text-right font-mono tabular text-body">
                    {/* The fee wallet's figure is its TRX (what pays the network), not its USDT. */}
                    {r.role === 'fee' ? (summary.data?.feeTrx != null ? fmtMoney(summary.data.feeTrx, 'TRX') : <span className="font-sans text-tg-hint">not known</span>) : r.totals.map((t) => (
                      <span key={t.asset} className="block">
                        {t.total === null ? <span className="font-sans text-tg-hint">not read</span> : fmtMoney(t.total, t.asset)}
                        {t.total !== null && t.read < t.all && <span className="ml-1 font-sans text-meta text-warn">{t.read} of {t.all} read</span>}
                      </span>
                    ))}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {/* 4 · TODAY — the Books' money movements of the Dubai day. */}
      <Panel title="Today" flush action={<Link to="/movements" className={TAP}>All moves →</Link>}>
        {readState(moves) === 'waiting' && !moves.data && <SkeletonRows rows={3} what="today's moves" />}
        {readState(moves) === 'failed' && <div className="px-4 py-3 lg:px-5"><ReadFailed what="today's moves" error={moves.error} onRetry={() => void moves.refetch()} /></div>}
        {moves.data && moves.data.rows.length === 0 && readState(moves) === 'known' && <EmptyState>No money moved today.</EmptyState>}
        {moves.data && moves.data.rows.length > 0 && (
          <ul>
            {moves.data.rows.slice(0, TODAY_SHOWN).map((m) => (
              <li key={m.txn_id} className="flex items-baseline gap-3 border-b border-stroke-soft px-4 py-3 last:border-b-0 lg:px-5">
                <span className="w-12 shrink-0 font-mono tabular text-meta text-tg-hint">{fmtTime(m.at)}</span>
                <span className="min-w-0 break-words text-body">{m.words}</span>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {openWallet ? <WalletMovesSheet w={openWallet} head={<ul><WalletLine w={openWallet} nameOf={nameOf} /></ul>} onBack={open && open.wallets.length > 1 ? () => setWallet(null) : null} onClose={() => setRole(null)} />
        : open && <RoleSheet r={open} nameOf={nameOf} onOpen={(id) => setWallet(id)} onClose={() => setRole(null)} />}
    </>
  );
}

/** A role's wallets: each one's name, address and balance as last read. */
function RoleSheet({ r, nameOf, onOpen, onClose }: { r: RoleRow; nameOf: (slug: string) => string; onOpen: (id: number) => void; onClose: () => void }) {
  return (
    <DetailSheet title={r.word} onClose={onClose}>
      <div className="space-y-4">
        {r.note && <StateLine>{r.note}</StateLine>}
        <ul className="space-y-3">
          {r.wallets.map((w) => <WalletLine key={w.id} w={w} nameOf={nameOf} onOpen={() => onOpen(w.id)} />)}
        </ul>
        <p className="flex flex-wrap gap-x-4 gap-y-2 text-body">
          <Link to="/movements" className={TAP}>Money movements →</Link>
          <Link to="/chain" className={TAP}>Chain transfers →</Link>
        </p>
      </div>
    </DetailSheet>
  );
}

function WalletLine({ w, nameOf, onOpen }: { w: WalletBalance; nameOf: (slug: string) => string; onOpen?: () => void }) {
  return (
    <li className="border-t border-stroke-soft pt-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 break-words text-body font-medium">{walletName(w)}</span>
        <span className="shrink-0 font-mono tabular text-body">{w.balance === null ? <span className="font-sans text-tg-hint">not read</span> : fmtMoney(w.balance, w.asset)}</span>
      </div>
      {/* THE WHOLE ADDRESS, wrapping (design system §5's exception: a wallet list exists to check one address against another). */}
      <p className="mt-1 break-all font-mono text-meta">{w.address}</p>
      <div className="mt-1 flex flex-wrap items-baseline gap-x-3 text-meta text-tg-hint">
        <span>{w.asOf ? `read ${relativeTime(w.asOf)}` : 'not read'}</span>
        {w.partySlug && <span>for {nameOf(w.partySlug)}</span>}
        {onOpen && <button type="button" className={`${TAP} ml-auto`} onClick={onOpen}>Moves →</button>}
      </div>
    </li>
  );
}
